import { useRef, useEffect, useCallback, useState, forwardRef, useImperativeHandle, type ReactNode } from "react";
import { TransformWrapper, TransformComponent, type ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

/** Bounding box in pixel coordinates */
export type BoundingBox = { left: number; top: number; width: number; height: number };

// Animation and zoom constants
const ANIMATION_DURATION_MS = 300;
const ZOOM_THRESHOLD = 1.05;

/**
 * Wraps content with zoom/pan capabilities.
 *
 * Main features:
 * - zoomToRegion: Directly pan/zoom to a bounding box region. Smoothly transitions
 *   from current view to the target region without zoom-out/zoom-in sequence.
 * - Cmd+scroll zooms toward cursor, regular scroll scrolls content.
 * - autoZoomToBox: Auto-zoom to a region on load (used by Photos.tsx).
 */
export interface ZoomableImageRef {
  zoomToRegion: (element: HTMLElement, region: BoundingBox, imageWidth: number, imageHeight: number) => void;
}

interface ZoomableImageProps {
  children: ReactNode;
  className?: string;
  showControls?: boolean;
  resetKey?: string | number;
  requireModifierForWheelZoom?: boolean;
  autoZoomToBox?: BoundingBox;
  imageDimensions?: { width: number; height: number };
  autoZoomScale?: number;
  preferredScale?: number;
  onScaleChange?: (scale: number) => void;
  maintainZoomOnPan?: boolean;
}

export const ZoomableImage = forwardRef<ZoomableImageRef, ZoomableImageProps>(function ZoomableImage(
  {
    children,
    className,
    showControls = true,
    resetKey,
    requireModifierForWheelZoom = false,
    autoZoomToBox,
    imageDimensions,
    autoZoomScale = 2,
    preferredScale,
    onScaleChange,
    maintainZoomOnPan = true,
  },
  ref,
) {
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastResetKeyRef = useRef<string | number | undefined>(resetKey);
  const hasInitialZoomedRef = useRef(false);

  const [isPositioned, setIsPositioned] = useState(!autoZoomToBox);
  const [isPanning, setIsPanning] = useState(false);
  const [isZoomedIn, setIsZoomedIn] = useState(false);

  const targetScale = preferredScale && preferredScale > 1 ? preferredScale : autoZoomScale;

  /**
   * Directly pans/zooms to center on a region within an image element.
   * Smoothly transitions from the current view to the target region.
   */
  const zoomToRegion = useCallback(
    (element: HTMLElement, region: BoundingBox, imageWidth: number, imageHeight: number) => {
      if (!transformRef.current) return;

      const { setTransform, state } = transformRef.current;
      const wrapperEl = transformRef.current.instance.wrapperComponent;
      if (!wrapperEl) return;

      const img = element.querySelector("img");
      if (!img) return;

      const { scale: currentScale, positionX, positionY } = state;
      const wrapperRect = wrapperEl.getBoundingClientRect();
      const imgRect = img.getBoundingClientRect();

      // Convert from transformed (screen) coordinates to untransformed (content) coordinates.
      // The library applies: translate(positionX, positionY) scale(scale)
      // So: screenPos = contentPos * scale + translatePos
      // Therefore: contentPos = (screenPos - translatePos) / scale
      const imgLeftRelativeToWrapper = imgRect.left - wrapperRect.left;
      const imgTopRelativeToWrapper = imgRect.top - wrapperRect.top;

      const imgContentLeft = (imgLeftRelativeToWrapper - positionX) / currentScale;
      const imgContentTop = (imgTopRelativeToWrapper - positionY) / currentScale;
      const imgContentWidth = imgRect.width / currentScale;
      const imgContentHeight = imgRect.height / currentScale;

      // Calculate region center in content coordinates
      const regionCenterX = imgContentLeft + imgContentWidth * ((region.left + region.width / 2) / imageWidth);
      const regionCenterY = imgContentTop + imgContentHeight * ((region.top + region.height / 2) / imageHeight);

      // Maintain current zoom level - don't force zoom on initial load
      // Only apply default zoom if user has already zoomed in slightly
      const newScale = currentScale > ZOOM_THRESHOLD ? currentScale : 1;

      // Calculate translation to center the region
      // Goal: regionCenter * newScale + newPosition = wrapperCenter
      const newPositionX = wrapperRect.width / 2 - regionCenterX * newScale;
      const newPositionY = wrapperRect.height / 2 - regionCenterY * newScale;

      setTransform(newPositionX, newPositionY, newScale, ANIMATION_DURATION_MS, "easeOut");
    },
    [],
  );

  useImperativeHandle(ref, () => ({ zoomToRegion }), [zoomToRegion]);

  const panToBoundingBox = useCallback(
    (useCurrentScale: boolean) => {
      if (!transformRef.current || !autoZoomToBox || !imageDimensions) return false;

      const { setTransform, state } = transformRef.current;
      const wrapperEl = transformRef.current.instance.wrapperComponent;
      if (!wrapperEl) return false;

      const { width: wrapperW, height: wrapperH } = wrapperEl.getBoundingClientRect();
      if (wrapperW === 0 || wrapperH === 0) return false;

      // Calculate how image fits in wrapper
      const imageAspect = imageDimensions.width / imageDimensions.height;
      const wrapperAspect = wrapperW / wrapperH;
      const [imgW, imgH] = imageAspect > wrapperAspect ? [wrapperW, wrapperW / imageAspect] : [wrapperH * imageAspect, wrapperH];
      const offsetX = (wrapperW - imgW) / 2;
      const offsetY = (wrapperH - imgH) / 2;

      // Calculate box center in wrapper coordinates
      const boxCenterX = offsetX + ((autoZoomToBox.left + autoZoomToBox.width / 2) / imageDimensions.width) * imgW;
      const boxCenterY = offsetY + ((autoZoomToBox.top + autoZoomToBox.height / 2) / imageDimensions.height) * imgH;

      const scale = useCurrentScale && maintainZoomOnPan && state.scale > 1 ? state.scale : targetScale;
      setTransform(wrapperW / 2 - boxCenterX * scale, wrapperH / 2 - boxCenterY * scale, scale, 0);
      setIsPositioned(true);
      return true;
    },
    [autoZoomToBox, imageDimensions, targetScale, maintainZoomOnPan],
  );

  // Handle resetKey changes and initial auto-zoom
  useEffect(() => {
    if (resetKey === lastResetKeyRef.current) return;
    const isFirstRender = lastResetKeyRef.current === undefined;
    lastResetKeyRef.current = resetKey;

    if (autoZoomToBox) setIsPositioned(false);

    if (autoZoomToBox && imageDimensions) {
      requestAnimationFrame(() => {
        if (panToBoundingBox(!isFirstRender)) hasInitialZoomedRef.current = true;
      });
    } else if (!isFirstRender && transformRef.current) {
      transformRef.current.centerView(1, 0);
    }
  }, [resetKey, autoZoomToBox, imageDimensions, panToBoundingBox]);

  // Initial auto-zoom when dimensions become available
  useEffect(() => {
    if (!autoZoomToBox || !imageDimensions || hasInitialZoomedRef.current) return;
    requestAnimationFrame(() => {
      if (panToBoundingBox(false)) hasInitialZoomedRef.current = true;
    });
  }, [autoZoomToBox, imageDimensions, panToBoundingBox]);

  // Retry auto-zoom on init (wrapper may not be ready immediately)
  const handleInit = useCallback(
    (r: ReactZoomPanPinchRef) => {
      transformRef.current = r;
      if (autoZoomToBox && imageDimensions && !hasInitialZoomedRef.current) {
        const attempt = (n: number) => {
          if (n <= 0 || hasInitialZoomedRef.current) return;
          if (panToBoundingBox(false)) hasInitialZoomedRef.current = true;
          else setTimeout(() => attempt(n - 1), 100);
        };
        setTimeout(() => attempt(5), 50);
      }
    },
    [autoZoomToBox, imageDimensions, panToBoundingBox],
  );

  // Reset zoom when container resizes significantly (e.g., sidebar drag)
  useEffect(() => {
    if (!wrapperRef.current) return;

    let lastWidth = wrapperRef.current.offsetWidth;
    let lastHeight = wrapperRef.current.offsetHeight;
    let debounceTimer: NodeJS.Timeout | null = null;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;
      const widthChanged = Math.abs(width - lastWidth) > 50;
      const heightChanged = Math.abs(height - lastHeight) > 50;

      // Only reset if size changed significantly
      if (widthChanged || heightChanged) {
        lastWidth = width;
        lastHeight = height;

        // Debounce to wait for resize to settle
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          if (transformRef.current) {
            transformRef.current.centerView(1, ANIMATION_DURATION_MS, "easeOut");
          }
        }, 150);
      }
    });

    observer.observe(wrapperRef.current);
    return () => {
      observer.disconnect();
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, []);

  useEffect(() => {
    if (!requireModifierForWheelZoom || !wrapperRef.current) return;

    let rafId: number | null = null;
    let pending: { deltaY: number; mouseX: number; mouseY: number } | null = null;

    const applyZoom = () => {
      rafId = null;
      if (!pending || !transformRef.current) return;
      const { setTransform, state } = transformRef.current;
      const { scale, positionX, positionY } = state;

      const zoomFactor = pending.deltaY < 0 ? 1.3 : 0.77;
      const newScale = Math.min(3, Math.max(1, scale * zoomFactor));
      const ratio = newScale / scale;

      setTransform(
        pending.mouseX - (pending.mouseX - positionX) * ratio,
        pending.mouseY - (pending.mouseY - positionY) * ratio,
        newScale,
        0,
      );
      pending = null;
    };

    const handleWheel = (e: WheelEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !transformRef.current) return;
      e.preventDefault();

      const rect = transformRef.current.instance.wrapperComponent?.getBoundingClientRect();
      if (!rect) return;

      pending = { deltaY: e.deltaY, mouseX: e.clientX - rect.left, mouseY: e.clientY - rect.top };
      if (rafId === null) rafId = requestAnimationFrame(applyZoom);
    };

    const el = wrapperRef.current;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", handleWheel);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [requireModifierForWheelZoom]);

  return (
    <div ref={wrapperRef} className={cn("relative overflow-hidden", className)} style={{ opacity: isPositioned ? 1 : 0 }}>
      <TransformWrapper
        ref={transformRef}
        initialScale={autoZoomToBox ? targetScale : 1}
        minScale={1}
        maxScale={3}
        centerOnInit={!autoZoomToBox}
        doubleClick={{ mode: "toggle", step: 2 }}
        wheel={{ smoothStep: 0.1, disabled: requireModifierForWheelZoom }}
        panning={{ disabled: !isZoomedIn }}
        onInit={handleInit}
        onTransformed={(_, s) => {
          onScaleChange?.(s.scale);
          setIsZoomedIn(s.scale > ZOOM_THRESHOLD);
        }}
        onPanningStart={() => setIsPanning(true)}
        onPanningStop={() => setIsPanning(false)}
      >
        <TransformComponent
          wrapperStyle={{ width: "100%", height: "100%", cursor: isZoomedIn ? (isPanning ? "grabbing" : "grab") : "default" }}
          contentStyle={{ width: "100%", height: "100%", backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
        >
          {children}
        </TransformComponent>
      </TransformWrapper>

      {showControls && (
        <div className="pointer-events-none absolute inset-x-0 top-2 z-10 flex justify-end pr-2">
          <div className="pointer-events-auto flex gap-1 rounded-lg bg-black/60 p-1">
            <button
              onClick={() => transformRef.current?.zoomIn()}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-white transition-colors hover:bg-white/20"
              aria-label="Zoom in"
            >
              <ZoomIn size={18} />
            </button>
            <button
              onClick={() => transformRef.current?.zoomOut()}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-white transition-colors hover:bg-white/20"
              aria-label="Zoom out"
            >
              <ZoomOut size={18} />
            </button>
            <button
              onClick={() => transformRef.current?.centerView(1, ANIMATION_DURATION_MS, "easeOut")}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-white transition-colors hover:bg-white/20"
              aria-label="Fit to view"
            >
              <RotateCcw size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
});
